import { motion } from "framer-motion";
import {
    Camera,
    ClipboardList,
    Image,
    MonitorUp,
    Percent,
    ScanLine,
    Star,
    Tag,
    Zap,
} from "lucide-react";
import { EASE, Reveal } from "./Reveal";

const PATHS = [
    {
        id: "upc",
        numeral: "01",
        title: "Scan / UPC",
        tagline: "Point at the barcode. We do the rest.",
        icon: ScanLine,
        steps: [
            { icon: ScanLine, text: "Scan the product barcode or enter its UPC." },
            { icon: Tag, text: "Add the product price and promotional offer." },
            { icon: Percent, text: "Select a highlight such as Sale or Limited Time." },
            { icon: Image, text: "Choose portrait or landscape." },
            { icon: Zap, text: "Generate the ad." },
            { icon: MonitorUp, text: "Publish it directly to your screen." },
        ],
    },
    {
        id: "photo",
        numeral: "02",
        title: "Photo",
        tagline: "Snap it on the shelf. It's an ad moments later.",
        icon: Camera,
        steps: [
            { icon: Camera, text: "Take a photo or upload a product image." },
            { icon: ClipboardList, text: "Enter the product details, price, and offer." },
            { icon: Star, text: "Select a promotional highlight." },
            { icon: Image, text: "Choose portrait or landscape." },
            { icon: Zap, text: "Generate the ad." },
            { icon: MonitorUp, text: "Publish it directly to your screen." },
        ],
    },
];

const stepVariant = {
    hidden: { opacity: 0, x: -18 },
    show: (i) => ({
        opacity: 1,
        x: 0,
        transition: { duration: 0.55, delay: 0.25 + i * 0.09, ease: EASE },
    }),
};

const PathCard = ({ path, index }) => (
    <Reveal delay={index} className="h-full">
        <article
            data-testid={`path-card-${path.id}`}
            className="group relative h-full overflow-hidden rounded-[2rem] border border-teal-900/5 bg-white p-8 shadow-xl shadow-teal-900/5 transition-shadow duration-500 hover:shadow-2xl hover:shadow-teal-900/10 sm:p-10"
        >
            <span
                aria-hidden
                className="text-outline font-display pointer-events-none absolute -right-4 -top-8 select-none text-[11rem] font-extrabold leading-none"
            >
                {path.numeral}
            </span>

            <div className="relative">
                <div className="mb-2 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-teal-600 text-white shadow-lg shadow-teal-600/30 transition-transform duration-500 group-hover:-rotate-6 group-hover:scale-110">
                    <path.icon className="h-7 w-7" />
                </div>
                <h3 className="font-display mt-4 text-2xl font-bold text-slate-900 sm:text-3xl">
                    {path.title}
                </h3>
                <p className="mt-1.5 text-sm font-medium text-slate-500">{path.tagline}</p>

                <ol className="relative mt-9">
                    <span
                        aria-hidden
                        className="absolute bottom-6 left-[21px] top-6 w-px bg-gradient-to-b from-teal-500/40 via-teal-500/15 to-transparent"
                    />
                    {path.steps.map((step, i) => (
                        <motion.li
                            key={step.text}
                            variants={stepVariant}
                            initial="hidden"
                            whileInView="show"
                            viewport={{ once: true, margin: "-40px" }}
                            custom={i}
                            className="relative flex items-center gap-4 py-3"
                        >
                            <span className="relative z-10 flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-teal-100 bg-[#f0fdfa] text-teal-700 transition-colors duration-300 group-hover:border-teal-200">
                                <step.icon className="h-5 w-5" />
                            </span>
                            <span className="text-[15px] font-medium leading-snug text-slate-700">
                                <span className="mr-2 font-display text-xs font-bold text-teal-600">
                                    {String(i + 1).padStart(2, "0")}
                                </span>
                                {step.text}
                            </span>
                        </motion.li>
                    ))}
                </ol>
            </div>
        </article>
    </Reveal>
);

export const HowItWorks = () => (
    <section id="how-it-works" className="relative bg-[#eefbf7] py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <Reveal className="mx-auto mb-14 max-w-2xl text-center sm:mb-20">
                <p className="mb-3 text-xs font-semibold uppercase tracking-[0.22em] text-teal-700">
                    How It Works
                </p>
                <h2 className="font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
                    Two Easy Ways to Create an Ad
                </h2>
                <p className="mt-4 text-base text-slate-500 sm:text-lg">
                    Start from a barcode or a photo — both roads end with your ad live on
                    the big screen.
                </p>
            </Reveal>

            <div className="grid gap-8 lg:grid-cols-2 lg:gap-10">
                {PATHS.map((path, i) => (
                    <PathCard key={path.id} path={path} index={i} />
                ))}
            </div>
        </div>
    </section>
);
